import { useCallback, useSyncExternalStore } from "react";
import { local } from "@/lib/storage";

/** How an inline drawing is shown (#379): shrunk to the block's width, or at its own size. */
export type ScaleMode = "fit" | "actual";

const KEY = "esi:drawing-scale";
const listeners = new Set<() => void>();

const read = (): ScaleMode => (local.get(KEY) === "actual" ? "actual" : "fit");

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab's choice applies here too.
  const onStorage = (event: StorageEvent) => {
    if (event.key === KEY) {
      listener();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

// Unavailable storage keeps the choice for this page only.
let fallback: ScaleMode | null = null;
const snapshot = (): ScaleMode => fallback ?? read();

/**
 * The person's choice for inline drawings (Mermaid, SVG and chart artefacts): `fit` by default,
 * remembered per browser like the other view preferences and shared by every drawing on the page.
 */
export function useScaleMode(): [ScaleMode, (mode: ScaleMode) => void] {
  const mode = useSyncExternalStore(subscribe, snapshot, () => "fit" as const);
  const set = useCallback((next: ScaleMode) => {
    local.set(KEY, next === "fit" ? null : next);
    fallback = read() === next ? null : next;
    for (const listener of listeners) {
      listener();
    }
  }, []);
  return [mode, set];
}
